import React, { ComponentProps, FormEventHandler, forwardRef, MouseEventHandler, PropsWithChildren, ReactNode, useCallback } from 'react'
import ModalBase from './ModalBase'
import ModalBody from './ModalBody'
import ModalContent from './ModalContent'
import ModalFooter, { ModalFooterActionProps } from './ModalFooter'
import ModalHeader from './ModalHeader'
import ModalImageThumbnail from './ModalImageThumbnail'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { formToObject } from 'lib/forms/formToObject'

type InheritedModalHeaderProps = Pick<ComponentProps<typeof ModalHeader>, 'title' | 'subtitle'>
type InheritedModalFooterProps = Pick<ComponentProps<typeof ModalFooter>, 'isPrimaryActionHidden' | 'isSecondaryActionHidden'>
type InheritedModalImageThumbnailProps = Partial<Pick<ComponentProps<typeof ModalImageThumbnail>, 'image' | 'imageParams'>>

interface Props extends ComponentProps<typeof ModalBase>, InheritedModalHeaderProps, InheritedModalFooterProps, InheritedModalImageThumbnailProps {
  isPrimaryActionDisabled?: boolean
  isSecondaryActionDisabled?: boolean
  /**
   * If provided, the primary action will be displayed.
   */
  primaryActionText?: string
  /**
   * If provided, the secondary action will be displayed.
   */
  secondaryActionText?: string
  /**
   * Sets the button type of the primary action.
   *
   * @default 'submit'
   *  */
  primaryActionType?: 'submit' | 'button'
  /**
   * Sets the button type of the secondary action.
   *  */
  secondaryActionType?: 'reset' | 'button'
  primaryActionHref?: string
  primaryActionTo?: string
  secondaryActionTo?: string
  /**
   * Extra node at the bottom of the header.
   */
  headerExtension?: ReactNode
  /**
   * `event.preventDefault()` is invoked.
   */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>, resolve: (result?: any) => any) => void;
  onReset?: FormEventHandler<HTMLFormElement>
  /**
   * Callback on back button click.
   *
   * If provided, enables the back button to be displayed.
   */
  onBack?: MouseEventHandler<HTMLButtonElement>
  onPrimaryActionClick?: ModalFooterActionProps['onClick']
  onSecondaryActionClick?: ModalFooterActionProps['onClick']
  'data-testid'?: string;
}

const FormModal = forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => {
  const {
    children,
    className,
    image,
    imageParams,
    height,
    tabletHeight,
    isOpen,
    isPrimaryActionDisabled,
    isPrimaryActionHidden,
    isSecondaryActionDisabled,
    isSecondaryActionHidden,
    primaryActionText,
    primaryActionHref,
    primaryActionTo,
    secondaryActionText,
    primaryActionType = 'submit',
    secondaryActionType,
    size,
    subtitle,
    title,
    onSubmit,
    onReset,
    onAfterClose,
    onAfterOpen,
    onBack,
    onClose,
    onPrimaryActionClick,
    onSecondaryActionClick,
    'data-testid': testId,
    mode,
  } = props

  const { resolve } = useModalElementContext<any>()

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    if (onSubmit) {
      onSubmit?.(event, resolve)
    } else {
      const formValues = formToObject(event.currentTarget)
      resolve(formValues)
    }
  }, [onSubmit, resolve])

  return <ModalBase
    mode={mode}
    className={className}
    height={height}
    tabletHeight={tabletHeight}
    isOpen={isOpen}
    size={size}
    onAfterClose={onAfterClose}
    onAfterOpen={onAfterOpen}
    onClose={onClose}
  >
    <form
      onSubmit={handleSubmission}
      onReset={onReset}
    >
      <ModalHeader
        title={title}
        subtitle={subtitle}
        onBackButtonClick={onBack}
        onCloseButtonClick={onClose}
        data-testid={testId}
      />
      <ModalBody ref={ref}>
        {image && <ModalImageThumbnail image={image} imageParams={imageParams} />}
        {!!children && <ModalContent>
          {children}
        </ModalContent>}
      </ModalBody>
      {(primaryActionText || secondaryActionText) && <ModalFooter
        isPrimaryActionHidden={isPrimaryActionHidden}
        isSecondaryActionHidden={isSecondaryActionHidden}
        primaryActionProps={{
          type: primaryActionType,
          children: primaryActionText,
          disabled: isPrimaryActionDisabled,
          href: primaryActionHref,
          to: primaryActionTo,
          onClick: onPrimaryActionClick,
        }}
        secondaryActionProps={{
          type: secondaryActionType,
          children: secondaryActionText,
          disabled: isSecondaryActionDisabled,
          onClick: onSecondaryActionClick,
        }}
      />}
    </form>
  </ModalBase>
})

export default FormModal
